/**
 * CR-5534: Add/Edit dialog for the Quality Approval Register.
 *
 * Customer admins use this to record that a supplier holds a given
 * accreditation, attach the certificate document, and set the expiry
 * date that drives the RAG status on the matrix.
 */
import { useEffect, useState } from 'react';
import { Loader2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { qualityAPI } from '../lib/api';
import type {
  ApprovalRegisterAccreditation,
  ApprovalRegisterSupplier,
  SupplierAccreditation,
} from '../lib/api';
import FileDropZone from '../components/common/FileDropZone';

export interface ApprovalRegisterEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  suppliers: ApprovalRegisterSupplier[];
  accreditations: ApprovalRegisterAccreditation[];
  existingBindingId?: number | null;
  prefillSupplierId?: number | null;
  prefillAccreditationId?: number | null;
  onSaved: () => void;
}

export function ApprovalRegisterEditDialog({
  open,
  onOpenChange,
  suppliers,
  accreditations,
  existingBindingId,
  prefillSupplierId,
  prefillAccreditationId,
  onSaved,
}: ApprovalRegisterEditDialogProps) {
  const isEdit = Boolean(existingBindingId);

  const [supplierId, setSupplierId] = useState<number | null>(null);
  const [accreditationId, setAccreditationId] = useState<number | null>(null);
  const [issuedDate, setIssuedDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  // CR-7171: an expiry left blank has to be a deliberate statement, not an
  // omission — a blank one nobody ticked is what put undated certificates on
  // the register looking compliant.
  const [noExpiryApplies, setNoExpiryApplies] = useState(false);
  const [certificateNumber, setCertificateNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [existingFileName, setExistingFileName] = useState('');
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);

  // Hydrate state when the dialog opens.
  useEffect(() => {
    if (!open) return;
    setFile(null);
    setExistingFileName('');
    if (existingBindingId) {
      setLoadingDetail(true);
      qualityAPI
        .getSupplierAccreditation(existingBindingId)
        .then((res) => {
          const b: SupplierAccreditation = res.data;
          setSupplierId(b.supplier);
          setAccreditationId(b.accreditation);
          setIssuedDate(b.issued_date ?? '');
          setExpiryDate(b.expiry_date ?? '');
          setNoExpiryApplies(Boolean(b.no_expiry_applies));
          setCertificateNumber(b.certificate_number ?? '');
          setNotes(b.notes ?? '');
          setExistingFileName(b.certificate_file_name ?? '');
        })
        .catch(() => toast.error('Failed to load accreditation'))
        .finally(() => setLoadingDetail(false));
    } else {
      setSupplierId(prefillSupplierId ?? null);
      setAccreditationId(prefillAccreditationId ?? null);
      setIssuedDate('');
      setExpiryDate('');
      setNoExpiryApplies(false);
      setCertificateNumber('');
      setNotes('');
    }
  }, [open, existingBindingId, prefillSupplierId, prefillAccreditationId]);

  const handleSubmit = async () => {
    if (!supplierId || !accreditationId) {
      toast.error('Supplier and accreditation are required');
      return;
    }
    if (!expiryDate && !noExpiryApplies) {
      // Same wording as the serializer's rejection, so the user reads one
      // sentence whichever guard they hit.
      toast.error(
        'Enter an expiry date, or tick No expiry applies to record that this accreditation does not expire.'
      );
      return;
    }
    const fd = new FormData();
    fd.append('supplier', String(supplierId));
    fd.append('accreditation', String(accreditationId));
    if (issuedDate) fd.append('issued_date', issuedDate);
    // Ticking the box wins: it is the explicit statement, and sending a
    // leftover date alongside it would contradict what the user just said.
    if (expiryDate && !noExpiryApplies) fd.append('expiry_date', expiryDate);
    fd.append('no_expiry_applies', String(noExpiryApplies));
    fd.append('certificate_number', certificateNumber);
    fd.append('notes', notes);
    if (file) fd.append('certificate_file', file);

    try {
      setSaving(true);
      if (isEdit && existingBindingId) {
        await qualityAPI.updateSupplierAccreditation(existingBindingId, fd);
        toast.success('Accreditation updated');
      } else {
        await qualityAPI.createSupplierAccreditation(fd);
        toast.success('Accreditation added');
      }
      onSaved();
      onOpenChange(false);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save accreditation';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Accreditation' : 'Add Accreditation'}</DialogTitle>
          <DialogDescription>
            Record which accreditation the supplier holds, the expiry date, and
            attach the supporting certificate.
          </DialogDescription>
        </DialogHeader>

        {loadingDetail ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Supplier *</Label>
                <Select
                  value={supplierId ? String(supplierId) : ''}
                  onValueChange={(v) => setSupplierId(parseInt(v, 10))}
                  disabled={isEdit}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select supplier" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Accreditation *</Label>
                <Select
                  value={accreditationId ? String(accreditationId) : ''}
                  onValueChange={(v) => setAccreditationId(parseInt(v, 10))}
                  disabled={isEdit}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select accreditation" />
                  </SelectTrigger>
                  <SelectContent>
                    {accreditations.map((a) => (
                      <SelectItem key={a.id} value={String(a.id)}>
                        {a.approval_group_name ? `${a.approval_group_name} → ${a.name}` : a.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Issued Date</Label>
                <Input
                  type="date"
                  value={issuedDate}
                  onChange={(e) => setIssuedDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Expiry Date {!noExpiryApplies && '*'}</Label>
                <Input
                  type="date"
                  value={noExpiryApplies ? '' : expiryDate}
                  disabled={noExpiryApplies}
                  onChange={(e) => setExpiryDate(e.target.value)}
                />
                <div className="flex items-center gap-2 pt-1">
                  <Checkbox
                    id="no-expiry-applies"
                    checked={noExpiryApplies}
                    onCheckedChange={(checked) => setNoExpiryApplies(checked === true)}
                  />
                  <Label
                    htmlFor="no-expiry-applies"
                    className="text-sm font-normal text-muted-foreground"
                  >
                    No expiry applies
                  </Label>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Certificate Number</Label>
              <Input
                value={certificateNumber}
                onChange={(e) => setCertificateNumber(e.target.value)}
                placeholder="e.g. ISO-9001-12345"
              />
            </div>

            <div className="space-y-2">
              <Label>Certificate File</Label>
              {existingFileName && !file && (
                <div className="text-sm text-muted-foreground">
                  Current file: <span className="font-medium">{existingFileName}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                {/* Customer review 11 Sep (drag and drop everywhere). */}
                <FileDropZone
                  size="compact"
                  accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
                  hint={file ? <>Selected: <span className="font-medium text-foreground">{file.name}</span></> : 'PDF, image, Word or Excel'}
                  onFiles={(files) => setFile(files[0])}
                  className="flex-1"
                  data-testid="approval-certificate-drop-zone"
                />
                {file && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setFile(null)}
                    title="Clear selection"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving || loadingDetail}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                {isEdit ? 'Save Changes' : 'Add Accreditation'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
